import React, { useEffect, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { themeOptions } from "../../app";
import { useQuery } from "@tanstack/react-query";
import useBackend from "../../hooks/use_backend";
import { generateInitialFormValidity, validateForm } from "../../enums/form_validators_enum";

function DialogForm({ selectedRow, onClose, handleConfirm, dialogAction, isPending = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [isValid, setIsValid] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const backend = useBackend();
  const [formData, setFormData] = useState({});
  const [validityResults, setValidityResults] = useState({});
  const [isValidating, setIsValidating] = useState(false);

  const columns = [
    {
      accessorKey: dialogAction.fnDataLabelKey,
      header: "",
      Cell: ({ cell }) => <Box sx={{ textAlign: "center" }}>{cell.getValue()}</Box>,
    },
  ];

  const { data, error, isLoading } = useQuery({
    queryKey: [dialogAction.queryKey, backend.fetch],
    queryFn: () => dialogAction.queryFn(backend.fetch),
    enabled: dialogAction.queryKey != null,
  });

  useEffect(() => {
    // initialize default form data
    setFormData(
      Object.keys(dialogAction.formConfig).reduce((acc, fieldName) => {
        acc[fieldName] = dialogAction.formConfig[fieldName].defaultValue;
        return acc;
      }, {}),
    );
    // initialize default validity state -- does not check async validation methods
    generateInitialFormValidity(dialogAction.formConfig, setValidityResults);
  }, [dialogAction.formConfig, backend, setFormData, selectedRow]);

  useEffect(() => {
    if (data) {
      setFilteredData(
        data.filter((item) => {
          return item[dialogAction.fnDataLabelKey].toLowerCase().includes(searchTerm.toLowerCase());
        }),
      );
    }
  }, [data, searchTerm, dialogAction.fnDataLabelKey]);

  /*useEffect(() => {
        selectedEntry ? setIsValid(true) : setIsValid(false);
    }, [selectedEntry]);

     */

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  function onRowSelectionChange(entry_key, label_key) {
    setIsValidating(true);
    setSelectedEntry(entry_key);
    updateFormData(dialogAction.fnDataAccessKey, entry_key);
    updateFormData(dialogAction.fnDataLabelKey, label_key);
    setIsValidating(false);
  }

  function updateFormData(fieldName, newValue) {
    setFormData((prevState) => ({
      ...prevState,
      [fieldName]: newValue,
    }));
  }

  const onformValueChange = async (e) => {
    if (!isValidating) {
      setIsValidating(true);
      const { name, type, value, checked } = e.target; // unpack
      const newValue = type === "checkbox" ? checked : value;
      updateFormData(name, newValue);
      // recheck validity
      await validateForm(name, newValue, dialogAction.formConfig, setValidityResults, backend);
      setIsValidating(false);
    }
  };

  useEffect(() => {
    let allValid = true;
    // Check if any field is invalid
    for (const fieldName of Object.keys(validityResults)) {
      if (!validityResults[fieldName].isValid) {
        allValid = false;
        break;
      }
    }
    setIsValid(allValid);
  }, [validityResults]);

  if (error) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <div>Error loading data</div>;
      </Box>
    );
  }

  return (
    <Box>
      <DialogTitle>{dialogAction.parseHeader(selectedRow)}</DialogTitle>
      <DialogContent>
        {Object.keys(dialogAction.formConfig).map((fieldName, index) => {
          const { type, label, defaultValue, required, validators } = dialogAction.formConfig[fieldName];
          // feel free to add custom field implementations
          if (fieldName in formData) {
            if (type === "str") {
              return (
                <TextField
                  key={index}
                  name={fieldName}
                  label={label}
                  value={formData[fieldName] || ""}
                  onChange={onformValueChange}
                  fullWidth
                  margin="normal"
                  required={required}
                  error={validityResults && validityResults[fieldName] ? !validityResults[fieldName].isValid : false}
                  helperText={validityResults && validityResults[fieldName] ? validityResults[fieldName].errorMsg : ""}
                />
              );
            } else if (type === "bool") {
              return (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      name={fieldName}
                      checked={formData[fieldName]}
                      onChange={onformValueChange}
                      color="primary"
                    />
                  }
                  label={label}
                />
              );
            }
          }
          return null;
        })}
        {dialogAction.queryKey != null ? (
          <>
            <TextField
              placeholder={dialogAction.searchPlaceholder}
              variant="outlined"
              fullWidth
              margin="normal"
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <MaterialReactTable
              columns={columns}
              data={filteredData}
              enableTopToolbar={false}
              enableTableHead={false}
              enableGlobalFilter={false}
              enableRowSelection={false} // Disables the default ugly checkboxes for row selection...
              enableMultiRowSelection={false}
              initialState={{
                density: "compact",
                pagination: {
                  pageSize: 5, // Set the initial rows per page to 5
                },
              }}
              state={{
                isLoading,
              }}
              muiTableBodyRowProps={({ row }) => {
                return {
                  onClick: () => {
                    onRowSelectionChange(
                      row.original[dialogAction.fnDataAccessKey],
                      row.original[dialogAction.fnDataLabelKey],
                    );
                  },
                  sx: {
                    backgroundColor:
                      selectedEntry === row.original[dialogAction.fnDataAccessKey]
                        ? themeOptions.palette.primary.main
                        : "inherit",
                    cursor: "pointer",
                  },
                };
              }}
              muiTableBodyCellProps={{
                sx: { textAlign: "center", padding: "8px" },
              }}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {isPending ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {onClose ? (
              <Button onClick={onClose} sx={{ color: "grey" }}>
                Cancel
              </Button>
            ) : null}
            {isValidating ? (
              <CircularProgress size={24} />
            ) : (
              <Button
                onClick={() => handleConfirm(selectedRow, dialogAction, formData)}
                color="primary"
                disabled={!isValid || (!selectedEntry && dialogAction.queryKey != null) || isPending}
              >
                Confirm
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Box>
  );
}

export default DialogForm;
